import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Stack, Box, Select, MenuItem } from "@mui/material";

/**
 * Props:
 *  - open, onClose, onSent (callback), order, client, orderNumber
 */
const SendViaWhatsApp = ({ open, onClose, onSent, order, client, orderNumber }) => {
  const [sending, setSending] = useState(false);
  const [lang, setLang] = useState("ar"); // 'ar' or 'en'

  // country codes list (add/remove codes as needed)
  const countryCodes = [
    { code: "+20", label: "EG +20" },
    { code: "+966", label: "SA +966" },
    { code: "+971", label: "AE +971" },
    { code: "+1", label: "US +1" },
    { code: "+44", label: "UK +44" },
    { code: "+249", label: "SD +249" },
    { code: "+212", label: "MA +212" },
    { code: "+974", label: "QA +974" },
    { code: "+965", label: "KW +965" },
  ];

  const rawPhone = client?.phone || order?.checkout?.phone || "";
  // try to extract leading +countryCode (1-4 digits)
  const extractCountry = (p) => {
    if (!p) return { code: "+20", rest: "" };
    const m = String(p).trim().match(/^\+(\d{1,4})(.*)$/);
    if (m) {
      return { code: `+${m[1]}`, rest: m[2].replace(/\D/g, "") };
    }
    // if starts with 00
    const m2 = String(p).trim().match(/^00(\d{1,4})(.*)$/);
    if (m2) return { code: `+${m2[1]}`, rest: m2[2].replace(/\D/g, "") };
    // fallback: remove non-digits and assume no prefix
    return { code: "+20", rest: String(p).replace(/\D/g, "").replace(/^0+/, "") };
  };

  const { code: initialCountryCode, rest: initialPhoneRest } = extractCountry(rawPhone);
  const [selectedCountryCode, setSelectedCountryCode] = useState(initialCountryCode);
  const [phone, setPhone] = useState(initialPhoneRest);

  // generate default message for selected language
  const getDefaultMessage = (L) => {
    const name = client?.name || client?.email || "";
    const status = order?.status || "";
    const addr = order?.address || order?.checkout?.address || "";
    const phoneDisplay = client?.phone || order?.checkout?.phone || "";
    const emailDisplay = client?.email || "";
    const ordNum = orderNumber || order?.id || "";
    if (L === "en") {
      return [
        `Hello ${name},`,
        `Thank you for shopping at DIDA MAX.`,
        ``,
        `Order details:`, 
        `Order number: ${ordNum}`, 
        `Status: ${status || "-"}`, 
        ``,
        `Customer details:`, 
        `Name: ${name || "-"}`, 
        `Phone: ${phoneDisplay || "-"}`, 
        `Email: ${emailDisplay || "-"}`, 
        `Address: ${addr || "-"}`, 
        `Country: ${order?.country || "-"}`, 
        ``,
        `You can track your order at:`, 
        `https://didamax.net/myorders`, 
        `(please login to view your orders)`, 
        ``,
        `Thank you for shopping with DIDA MAX`
      ].join("\n");
    }
    // default Arabic
    return [
      `مرحباً ${name}،`,
      `شكراً لشرائك من DIDA MAX.`,
      ``,
      `بيانات الطلب:`, 
      `رقم الطلب: ${ordNum}`, 
      `الحالة: ${status || "-"}`, 
      ``,
      `بيانات العميل:`, 
      `الاسم: ${name || "-"}`, 
      `الهاتف: ${phoneDisplay || "-"}`, 
      `البريد: ${emailDisplay || "-"}`, 
      `العنوان: ${addr || "-"}`, 
      `الدولة: ${order?.country || "-"}`, 
      ``,
      `يمكنك متابعة حالة الطلب من خلال الرابط التالي:`, 
      `https://didamax.net/myorders`, 
      `(يرجى تسجيل الدخول لمشاهدة الطلبات)`, 
      ``,
      `شكراً لتسوقك معنا في DIDA MAX`
    ].join("\n");
  };

  const [message, setMessage] = useState(getDefaultMessage(lang));

  // sync when props change
  React.useEffect(() => {
    const { code, rest } = extractCountry(client?.phone || order?.checkout?.phone || "");
    setSelectedCountryCode(code);
    setPhone(rest);
    setMessage(getDefaultMessage(lang));
  }, [open, rawPhone, getDefaultMessage]);

  // update message when language changes while dialog open
  React.useEffect(() => {
    if (open) setMessage(getDefaultMessage(lang));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const handleSend = () => {
    if (!phone) {
      // still allow user to close
      onSent?.(false);
      onClose?.();
      return;
    }
    setSending(true);
    try {
      // build final phone: if user entered full number including +prefix use it, otherwise prefix selectedCountryCode
      const p = String(phone).trim();
      let finalNumber = "";
      if (p.startsWith("+")) {
        finalNumber = p.replace(/[^0-9]/g, "");
      } else {
        // remove leading zeros from national number
        const national = p.replace(/\D/g, "").replace(/^0+/, "");
        finalNumber = String(selectedCountryCode).replace(/\D/g, "") + national;
      }
      const text = encodeURIComponent(message);
      const url = `https://wa.me/${finalNumber}?text=${text}`;
      window.open(url, "_blank");
      // consider as sent/attempted
      onSent?.(true);
    } catch (err) {
      onSent?.(false);
    } finally {
      setSending(false);
      onClose?.();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>إرسال تحديث عبر واتساب</DialogTitle>
      <DialogContent>
        <Stack spacing={1} sx={{ mt: 1 }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Typography variant="caption" sx={{ mr: 1 }}>اللغة / Language</Typography>
            <Select size="small" value={lang} onChange={(e) => setLang(e.target.value)} sx={{ minWidth: 120 }}>
              <MenuItem value="ar">العربية</MenuItem>
              <MenuItem value="en">English</MenuItem>
            </Select>
          </Box>
          <Typography variant="caption">رقم الهاتف (بدون مسافات، يمكن أن يبدأ ب +)</Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Select size="small" value={selectedCountryCode} onChange={(e) => setSelectedCountryCode(e.target.value)} sx={{ minWidth: 120 }}>
              {countryCodes.map((cc) => (
                <MenuItem key={cc.code} value={cc.code}>{cc.label}</MenuItem>
              ))}
            </Select>
            <TextField size="small" value={phone} onChange={(e) => setPhone(e.target.value)} sx={{ flex: 1 }} />
          </Box>
          <Typography variant="caption">نص الرسالة</Typography>
          <TextField size="small" multiline minRows={4} value={message} onChange={(e) => setMessage(e.target.value)} fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { onClose?.(); onSent?.(false); }} disabled={sending}>إلغاء</Button>
        <Button variant="contained" onClick={handleSend} disabled={sending}>
          {sending ? "جارٍ الإرسال..." : "إرسال عبر واتساب"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendViaWhatsApp;
